<script lang="ts" setup>
import GravityForm from "~/components/GravityForm.vue";
import GridItem from "~/components/HouseAndLandPackages/GridItem.vue";
import {useEstates, useRegions, useSuburbs, useStatuses, useHouseLengths} from "~/composables/use-taxonomy";
import useHouseAndLandPackages, {
  useFilteredHouseAndLandPackages,
  useMaxBlockWidth,
  useMaxHouseSize
} from "~/composables/use-house-land-package";
import type {HouseLandPackage} from "~/graphql/house-land-packages";
import type {HouseLandPackagesCollectionItem} from "@nuxt/content";
const priceRangesAcf: any = await useAcfOptions('price_range');

const perPage = 6;
const props = defineProps<{
  pageSize: number,
  showFilters: boolean,
  showLoadMore: boolean,
  gravityForm?: string[],
}>();

const regions = await useRegions();
const suburbs = await useSuburbs();
const estates = await useEstates();
const statuses = await useStatuses();
const houseLengths = await useHouseLengths();
const maxHouseSize = await useMaxHouseSize();
const maxBlockWidth = await useMaxBlockWidth();
const allItems = await useHouseAndLandPackages()
const maxPage = Math.ceil((allItems.length || 6) / perPage);


const page = ref(1);
const bed = ref(0);
const car = ref(0);
const bath = ref(0);
const price = ref(0);
const storey = ref(0);
const suburb = ref(0);
const region = ref(0);
const estate = ref(0);
const status = ref(0);
const houseLength = ref(0);
const loading = ref(false);
const houseSize = ref(maxHouseSize);
const blockWidth = ref(maxBlockWidth);
const items = ref(allItems.slice(0, perPage));

const selectedPackage = ref({
  name: '',
  variation: '',
  suburb: ''
});

const handleEnquire = (packageData: { name: string, variation: string, suburb: string }) => {
  selectedPackage.value = packageData;
};

const filterItems = async () => {
  const filters = {
    region: region?.value,
    estate: estate?.value,
    suburb: suburb?.value,
    bed: bed?.value,
    car: car?.value,
    bath: bath?.value,
    price: price?.value,
    storey: storey?.value,
    houseSize: houseSize?.value,
    blockWidth: blockWidth?.value,
    status: status?.value,
    houseLength: houseLength?.value,
  };

  items.value = await useFilteredHouseAndLandPackages(filters, (page.value * perPage), priceRangesAcf);
}

const applyFilters = async () => {
  page.value = 1;
  await filterItems();
  loading.value = false;
}

const loadMore = () => {
  loading.value = true;
  let timer = setTimeout(() => {
    clearTimeout(timer);
    page.value++;
    filterItems();
    loading.value = false;
  }, 1000);
}

// preload all sub items
allItems?.forEach((pkg: HouseLandPackage | HouseLandPackagesCollectionItem) => {
  prerenderRoutes([pkg.uri]);
});
</script>

<template>
  <div class="w-100">
    <div class="container-xl house-land-packages-grid">
      <div v-if="props.showFilters" class="row package-filters">
        <div class="col-12">
          <div class="card p-3 pb-0 border-0 rounded-lg">
            <div class="card-body">
              <div class="row gx-4 mb-4 mb-md-0">
                <div class="mb-3 col-12 col-md-4 col-xl-2">
                  <select
                      name="regions"
                      @change="applyFilters"
                      v-model.number="region"
                      class="form-select shadow-none rounded-pill text-secondary bg-primary px-3 py-2 border-0">
                    <option value="0" :selected="region === 0">All Regions</option>
                    <option
                        v-for="v in regions" :value="v.databaseId"
                        :selected="region === v.databaseId">{{ v.name }}
                    </option>
                  </select>
                </div>
                <div class="mb-3 col-12 col-md-4 col-xl-2">
                  <select
                      name="estates"
                      @change="applyFilters"
                      v-model.number="estate"
                      class="form-select shadow-none rounded-pill text-secondary bg-primary px-3 py-2 border-0">
                    <option value="0" :selected="region === 0">All Estates</option>
                    <option
                        v-for="v in estates" :value="v.databaseId"
                        :selected="estate === v.databaseId">{{ v.name }}
                    </option>
                  </select>
                </div>
                <div class="mb-3 col-12 col-md-4 col-xl-2">
                  <select
                      name="suburbs"
                      @change="applyFilters"
                      v-model.number="suburb"
                      class="form-select shadow-none rounded-pill text-secondary bg-primary px-3 py-2 border-0">
                    <option value="0" :selected="region === 0">All Suburbs</option>
                    <option
                        v-for="v in suburbs" :value="v.databaseId"
                        :selected="suburb === v.databaseId">{{ v.name }}
                    </option>
                  </select>
                </div>
                <div class="mb-3 col-12 col-md-4 col-xl-2">
                  <select
                      name="prices"
                      @change="applyFilters"
                      v-model.number="price"
                      class="form-select shadow-none rounded-pill text-secondary bg-primary px-3 py-2 border-0">
                    <option value="0">Price Range</option>
                    <option
                        v-for="v in priceRangesAcf" :value="v.label"
                        :selected="price === v.label">{{ v.label }}
                    </option>
                  </select>
                </div>
                <div class="mb-3 col-12 col-md-4 col-xl-2">
                  <select
                      name="statuses"
                      v-model.number="status"
                      @change="applyFilters"
                      class="form-select shadow-none rounded-pill text-secondary bg-primary px-3 py-2 border-0">
                    <option value="0" :selected="status === 0">All Status</option>
                    <option
                        v-for="v in statuses" :value="v.databaseId"
                        :selected="status === v.databaseId">{{ v.name }}
                    </option>
                  </select>
                </div>
                <div class="col-12 col-md-4 col-xl-2">
                  <select
                      name="another"
                      v-model.number="houseLength"
                      @change="applyFilters"
                      class="form-select shadow-none rounded-pill text-secondary bg-primary px-3 py-2 border-0">
                    <option value="0">Any House Length</option>
                    <option
                        v-for="v in houseLengths" :value="v.databaseId"
                        :selected="houseLength === v.databaseId">{{ v.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="row gx-4">
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 mb-3 px-3">
                  <label for="block-width" class="form-label mb-0">Block Width (m)</label>
                  <input id="block-width" type="range" class="form-range border-0 shadow-none"
                         @change="applyFilters"
                         v-model.number="blockWidth"
                         :min="1" :step="1" :max="maxBlockWidth">
                  <div class="w-100 d-flex justify-content-between align-items-center">
                    <span class="text-dark">1</span>
                    <span class="text-dark">{{ blockWidth }}</span>
                  </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 mb-3 px-3">
                  <label for="house-size" class="form-label mb-0">House Size (m²)</label>
                  <input id="house-size" type="range" class="form-range border-0 shadow-none"
                         @change="applyFilters"
                         v-model.number="houseSize"
                         :min="1" :step="1" :max="maxHouseSize">
                  <div class="w-100 d-flex justify-content-between align-items-center">
                    <span class="text-dark">1</span>
                    <span class="text-dark">{{ houseSize }}</span>
                  </div>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 mb-3 px-3">
                  <select
                      name="bed"
                      v-model.number="bed"
                      @change="applyFilters"
                      class="bg-transparent px-0 border-0 border-bottom form-select rounded-0 shadow-none">
                    <option value="0" selected>Bed</option>
                    <option value="1">1 Bed Room</option>
                    <option value="2">2 Bed Rooms</option>
                    <option value="3">3 Bed Rooms</option>
                    <option value="4">4 Bed Rooms</option>
                    <option value="5">5 Bed Rooms</option>
                  </select>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 mb-3 px-3">
                  <select
                      name="bath"
                      v-model.number="bath"
                      @change="applyFilters"
                      class="bg-transparent px-0 border-0 border-bottom form-select rounded-0 shadow-none">
                    <option value="0" selected>Bath</option>
                    <option value="1">1 Bathroom</option>
                    <option value="2">2 Bathrooms</option>
                    <option value="3">3 Bathrooms</option>
                    <option value="4">4 Bathrooms</option>
                    <option value="5">5 Bathrooms</option>
                  </select>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 mb-3 px-3">
                  <select
                      name="car"
                      v-model.number="car"
                      @change="applyFilters"
                      class="bg-transparent px-0 border-0 border-bottom form-select rounded-0 shadow-none">
                    <option value="0" selected>Car</option>
                    <option value="1">1 Car Space</option>
                    <option value="2">2 Car Spaces</option>
                    <option value="3">3 Car Spaces</option>
                  </select>
                </div>
                <div class="col-12 col-md-6 col-lg-4 col-xl-2 mb-3 px-3">
                  <select
                      name="storey"
                      @change="applyFilters"
                      v-model.number="storey"
                      class="bg-transparent px-0 border-0 border-bottom form-select rounded-0 shadow-none">
                    <option value="0" selected>Storeys</option>
                    <option value="1">Single</option>
                    <option value="2">Double</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="items" class="row d-flex align-items-stretch g-md-5 mt-md-2 mt-5">
        <div v-for="v in items" class="col-12 col-md-6">
          <GridItem :item="v" popup="#enquire-modal" @enquire="handleEnquire"/>
        </div>
        <div v-if="props.showLoadMore && items.length > 0 && page < maxPage" class="section-spacer mt-0"></div>
        <div v-if="props.showLoadMore && items.length > 0 && page < maxPage"
             class="d-grid mt-0 d-md-flex justify-content-md-center">
          <button
              type="button"
              :disabled="loading"
              @click.prevent="loadMore"
              class="text-center btn btn-primary text-uppercase rounded-pill px-5 load-more">
            <span v-if="loading">
              <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
              <span role="status" class="ms-2">Loading...</span>
            </span>
            <span v-else>Load More</span>
          </button>
        </div>
        <div class="section-spacer mt-0"></div>
      </div>
    </div>
    <div v-if="props?.gravityForm && props.gravityForm[0]"
         class="modal fade" id="enquire-modal" tabindex="-1" aria-labelledby="enquire-modal-label" aria-hidden="true"
         :data-package-name="selectedPackage.name"
         :data-package-variation="selectedPackage.variation"
         :data-package-suburb="selectedPackage.suburb">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h1 class="modal-title fs-5" id="enquire-modal-label">Enquire</h1>
            <button type="button" class="btn-close border-0 shadow-none" data-bs-dismiss="modal"
                    aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <GravityForm
              formId="enquiry-form"
              :gravityFormId="parseInt(props?.gravityForm[0])"
              class="hlenquiry"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped>
.package-filters {
  .card {
    box-shadow: 0 0 7px #35353561;
  }
}

.load-more:disabled {
  background-color: var(--bs-primary);
  border-color: var(--bs-primary);
}

.spinner-border-sm {
  border-width: 1px;
}

.form-select {
  &.rounded-pill {
    color: var(--bs-secondary);
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
  }

  &.px-0 {
    padding-top: 0 !important;
    padding-bottom: .7rem !important;
    background-position: right 5px top 5px !important;
    border-bottom-color: var(--var-dark-color) !important;
  }
}

.form-label {
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
}

.form-range {
  padding: 10px 0 !important;

  &::-webkit-slider-runnable-track {
    height: 1px;
    background-color: var(--var-dark-color);
  }

  &::-webkit-slider-thumb {
    width: 8px;
    height: 8px;
    margin-top: -4px;
    background-color: var(--var-dark-color);
  }

  &:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 1px #fff, 0 0 0 .25rem rgba(56, 70, 53, .3);
  }
}
</style>
